import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import ShareWithNetworkDrawerItem from '@app/src/pages/Questionnaire/Provider/Actions/ShareWithNetworkDrawerItem'
import useProviderInquiries from '@app/src/pages/Questionnaire/Provider/useProviderInquiries'
import type { SimpleOrganization } from '@app/src/types/organizations'
import type { ReportAccessModel, Request } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS, WF_ORGANIZATION_ID } from '@app/src/wf-constants'
import { Alert, alpha, Box, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'
import type { DrawerViewSubmitQuestionnaireProps } from './DrawerViewSubmitQuestionnaire'
import DrawerViewSubmitQuestionnaire from './DrawerViewSubmitQuestionnaire'
import {
  isContinueDisabled,
  organizationIdsSelectedByDefault,
  organizationIdsToShareWithOnSubmit,
} from './submitShareTargets'

type DrawerViewSubmitShareQuestionnaireProps = {
  request?: Request
  currentAccess?: ReportAccessModel
} & Pick<DrawerViewSubmitQuestionnaireProps, 'onSubmit'> &
  Omit<DrawerViewProps, 'title' | 'subtitle'>

const DrawerViewSubmitShareQuestionnaire: React.FC<DrawerViewSubmitShareQuestionnaireProps> = ({
  request,
  currentAccess,
  onSubmit,
  ...props
}) => {
  const { openDrawer } = useDrawer()
  const { formatMessage } = useIntl()
  const [accessorOrganizationIds, setAccessorOrganizationIds] = useState<number[]>([])
  // Drawers render outside ProviderReportContext, so read the inquiries from the shared query instead
  const { data: providerInquiries, isLoading: isLoadingProviderInquiries } = useProviderInquiries(request)

  // Fetch accessor organizations that have the responder organization as provider
  const { data: accessorOrganizations, isFetching: isFetchingAccessors } = useFetchResource<SimpleOrganization[]>({
    key: [FetchKey.AccessorOrganization],
    endpoint: endpoints.accessors,
    options: { staleTime: ONE_MINUTE_MS },
  })

  const isWorldfavorStandard = request?.template.creatorOrganizationId === WF_ORGANIZATION_ID
  const organizationIdsWithAccess = useMemo(() => currentAccess?.organizationIds ?? [], [currentAccess]) // RequestSubscription > CreatorOrganizationId
  const requestingOrganizationIds = useMemo(
    () => providerInquiries?.items?.map(i => i.creatorOrganizationId) ?? [],
    [providerInquiries],
  )
  const allowGroupNetworkRequestSharing = request?.template?.allowGroupNetworkRequestSharing ?? false

  const [isSharedWithAllEnabled, setIsSharedWithAllEnabled] = useState<boolean>(
    Boolean(currentAccess?.isSharedWithTemplateAuthorizedOrgs),
  )

  const organizationsToDisplay = useMemo(() => {
    const hasTargetProduct = request?.targetProductId != null

    return (
      // For all accessors with the responder organization as provider
      accessorOrganizations
        ?.filter(organization => {
          // For product-linked requests, only show organizations that already have access
          if (hasTargetProduct) {
            return organizationIdsWithAccess.includes(organization.id)
          }

          // Normal filtering for non-product requests
          return isWorldfavorStandard
            ? true
            : // If accessor either have an inquiry or subscription then include it
              organizationIdsWithAccess.includes(organization.id) || requestingOrganizationIds.includes(organization.id)
        })
        ?.map(o => ({
          ...o,
          isRequested: isWorldfavorStandard
            ? requestingOrganizationIds.includes(o.id)
            : // If accessor either have an inquiry or subscription then show 'Requested' badge
              organizationIdsWithAccess.includes(o.id) || requestingOrganizationIds.includes(o.id),
          hasAccess: organizationIdsWithAccess.includes(o.id),
          // If 'Share with all' is toggled or accessor has a subscription then disable it
          isDisabled: isSharedWithAllEnabled || organizationIdsWithAccess.includes(o.id),
        }))
        ?.sort((org1, org2) => {
          if (org1.isDisabled && !org2.isDisabled) return 1
          else if (!org1.isDisabled && org2.isDisabled) return -1

          if (org1.isRequested && !org2.isRequested) return -1
          else if (!org1.isRequested && org2.isRequested) return 1

          return 0
        }) ?? []
    )
  }, [accessorOrganizations, requestingOrganizationIds, isSharedWithAllEnabled, organizationIdsWithAccess, request])

  const handleSelect = (checkedAccessorId: number) => {
    const newIds = accessorOrganizationIds.includes(checkedAccessorId)
      ? accessorOrganizationIds.filter(id => id !== checkedAccessorId)
      : [...accessorOrganizationIds, checkedAccessorId]
    setAccessorOrganizationIds(newIds)
  }

  const defaultSelectedOrganizationIds = useMemo(
    () => organizationIdsSelectedByDefault(organizationsToDisplay),
    [organizationsToDisplay],
  )

  const isLoadingShareTargets = isFetchingAccessors || isLoadingProviderInquiries

  useEffect(() => {
    if (isLoadingShareTargets) return

    if (isSharedWithAllEnabled) {
      setAccessorOrganizationIds(accessorOrganizations?.map(o => o.id) ?? [])
    } else {
      setAccessorOrganizationIds(defaultSelectedOrganizationIds)
    }
  }, [isLoadingShareTargets, isSharedWithAllEnabled, accessorOrganizations, defaultSelectedOrganizationIds])

  const showShareCount = !isSharedWithAllEnabled && accessorOrganizationIds.length > 0

  return (
    <DrawerView
      title={formatMessage({ id: 'reporting.submitValidation' })}
      subTitle={request?.title}
      {...props}
      buttons={[
        {
          label: showShareCount
            ? formatMessage({ id: 'reporting.sharing.shareWithCompanies' }, { count: accessorOrganizationIds.length })
            : formatMessage({ id: 'general.continue' }),
          disabled: isContinueDisabled({
            isLoading: isLoadingShareTargets,
            isSharedWithAllEnabled,
            selectedCount: accessorOrganizationIds.length,
            shareableCount: organizationsToDisplay.length,
          }),
          variant: 'contained',
          onClick: () =>
            openDrawer(
              <DrawerViewSubmitQuestionnaire
                isSharedWithTemplateAuthorizedOrgs={currentAccess?.isSharedWithTemplateAuthorizedOrgs}
                isStandardQuestionnaire={isWorldfavorStandard}
                onSubmit={onSubmit}
                sharingProps={{
                  requestId: request?.id ?? 0,
                  organizationIdsToShareWith: organizationIdsToShareWithOnSubmit({
                    isSharedWithAllEnabled,
                    selectedOrganizationIds: accessorOrganizationIds,
                  }),
                  isSharedWithTemplateAuthorizedOrgs: isSharedWithAllEnabled,
                }}
              />,
            ),
        },
      ]}
    >
      <Stack px={2}>
        <Typography paragraph>{formatMessage({ id: 'reporting.sharing.submitSharingInfo' })}</Typography>

        {(isWorldfavorStandard || allowGroupNetworkRequestSharing) && (
          <Box border={`1px solid ${alpha('#000', 0.12)}`} borderRadius={2} px={2}>
            <ShareWithNetworkDrawerItem
              isChecked={isSharedWithAllEnabled}
              onClick={() => setIsSharedWithAllEnabled(prev => !prev)}
              isCustomerNetwork={allowGroupNetworkRequestSharing}
              showLearnMore={isWorldfavorStandard && !isSharedWithAllEnabled}
              requestTitle={request?.title}
            />
            {isSharedWithAllEnabled && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSharedWithAllEnabled}
                    onChange={(_, checked): void => setIsSharedWithAllEnabled(checked)}
                  />
                }
                labelPlacement="end"
                label={
                  isWorldfavorStandard
                    ? formatMessage({ id: 'reporting.sharing.worldfavorNetworkCheckbox' })
                    : formatMessage(
                        { id: 'reporting.sharing.groupNetworkCheckbox' },
                        { companyName: request?.template?.creatorOrganization?.name },
                      )
                }
                sx={{ alignItems: 'flex-start', pb: 1.5 }}
              />
            )}
          </Box>
        )}

        <Box mt={3}>
          <Typography color="textSecondary">{formatMessage({ id: 'reporting.sharing.shareWith' })}</Typography>

          {organizationsToDisplay?.map(organization => (
            <Box key={organization.id}>
              <FormControlLabel
                disabled={organization.isDisabled}
                control={
                  <Checkbox
                    color="primary"
                    checked={accessorOrganizationIds.includes(organization.id)}
                    onChange={() => handleSelect(organization.id)}
                    disabled={organization.isDisabled}
                  />
                }
                label={
                  <Box display="flex">
                    <Typography variant="body1">{organization.name}</Typography>
                    {organization.isRequested && (
                      <Chip
                        disabled={organization.isDisabled}
                        size="small"
                        color="info"
                        label={formatMessage({ id: 'reporting.sharing.sharingRequested' })}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                }
              />
            </Box>
          ))}
        </Box>
      </Stack>

      <Alert severity="info" variant="filled" sx={{ mt: 3, borderRadius: 0 }}>
        {formatMessage({ id: 'reporting.sharing.cannotUnshareInfo' })}
      </Alert>
    </DrawerView>
  )
}

export default DrawerViewSubmitShareQuestionnaire
